import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
    DiagnosticEntry,
    DiagnosticMemorySnapshot,
    DiagnosticMetric,
    DiagnosticMetricUnit,
    DiagnosticScope,
    DiagnosticSummaryItem,
} from './diagnostic-types';

const MAX_ENTRIES = 1000;
const DEFAULT_GROUP = 'application';
const DECIMAL_PLACES = 2;

const NOOP_DIAGNOSTIC_SCOPE: DiagnosticScope = {
    addMetric: (): void => {},
    end: (): void => {},
    fail: (): void => {},
};

interface InternalDiagnosticScopeState {
    readonly id: number;
    readonly name: string;
    readonly group: string;
    readonly startedAt: number;
    readonly metadata?: Record<string, unknown>;
    readonly memoryStart?: DiagnosticMemorySnapshot;
    readonly metrics: DiagnosticMetric[];
    closed: boolean;
}

@Injectable({ providedIn: 'root' })
export class BrowserDiagnosticsService {
    private readonly enabled = environment.production !== true
        && environment.features?.enableDiagnostics === true;

    private readonly entries: DiagnosticEntry[] = [];

    private nextId = 1;

    public constructor() {
        if (this.enabled && typeof window !== 'undefined') {
            window.__fitogestDiagnostics = {
                getEntries: () => this.getEntries(),
                summary: () => this.summary(),
                printSummary: () => this.printSummary(),
                clear: () => this.clear(),
            };
        }
    }

    public isEnabled(): boolean {
        return this.enabled;
    }

    public start(
        name: string,
        group: string = DEFAULT_GROUP,
        metadata?: Record<string, unknown>,
    ): DiagnosticScope {
        if (!this.enabled) {
            return NOOP_DIAGNOSTIC_SCOPE;
        }

        const memoryStart = this.readMemory();
        const state: InternalDiagnosticScopeState = {
            id: this.nextId,
            name,
            group,
            startedAt: this.now(),
            ...(metadata !== undefined ? { metadata } : {}),
            ...(memoryStart !== undefined ? { memoryStart } : {}),
            metrics: [],
            closed: false,
        };

        this.nextId += 1;

        return {
            addMetric: (metricName: string, value: number, unit: DiagnosticMetricUnit = 'custom'): void => {
                if (!this.enabled || state.closed || !Number.isFinite(value)) {
                    return;
                }

                state.metrics.push({ name: metricName, value, unit });
            },
            end: (endMetadata?: Record<string, unknown>): void => {
                this.closeScope(state, endMetadata);
            },
            fail: (error: unknown, endMetadata?: Record<string, unknown>): void => {
                this.closeScope(state, endMetadata, this.stringifyError(error));
            },
        };
    }

    public getEntries(): DiagnosticEntry[] {
        return this.entries.map((entry) => ({
            ...entry,
            metrics: [...entry.metrics],
        }));
    }

    public clear(): void {
        this.entries.length = 0;
    }

    public summary(): DiagnosticSummaryItem[] {
        const grouped = new Map<string, DiagnosticSummaryItem>();

        for (const entry of this.entries) {
            const key = `${entry.group}::${entry.name}`;
            const current = grouped.get(key);

            if (!current) {
                grouped.set(key, {
                    name: entry.name,
                    group: entry.group,
                    calls: 1,
                    totalMs: this.round(entry.durationMs),
                    averageMs: this.round(entry.durationMs),
                    maxMs: this.round(entry.durationMs),
                    errors: entry.error ? 1 : 0,
                });
                continue;
            }

            const calls = current.calls + 1;
            const totalMs = current.totalMs + entry.durationMs;

            grouped.set(key, {
                name: current.name,
                group: current.group,
                calls,
                totalMs: this.round(totalMs),
                averageMs: this.round(totalMs / calls),
                maxMs: this.round(Math.max(current.maxMs, entry.durationMs)),
                errors: current.errors + (entry.error ? 1 : 0),
            });
        }

        return [...grouped.values()].sort((a, b) => b.totalMs - a.totalMs);
    }

    public printSummary(): void {
        if (!this.enabled) {
            console.warn('[Fitogest diagnostics] Disabled. Set environment.features.enableDiagnostics = true in a non-production build.');
            return;
        }

        console.log(this.summary());
    }

    private closeScope(
        state: InternalDiagnosticScopeState,
        metadata?: Record<string, unknown>,
        error?: string,
    ): void {
        if (!this.enabled || state.closed) {
            return;
        }

        state.closed = true;

        const endedAt = this.now();
        const mergedMetadata = {
            ...(state.metadata ?? {}),
            ...(metadata ?? {}),
        };
        const memoryEnd = this.readMemory();

        const entry: DiagnosticEntry = {
            id: state.id,
            name: state.name,
            group: state.group,
            startedAt: state.startedAt,
            endedAt,
            durationMs: this.round(endedAt - state.startedAt),
            ...(Object.keys(mergedMetadata).length > 0 ? { metadata: mergedMetadata } : {}),
            metrics: [...state.metrics],
            ...(state.memoryStart !== undefined ? { memoryStart: state.memoryStart } : {}),
            ...(memoryEnd !== undefined ? { memoryEnd } : {}),
            ...(error !== undefined ? { error } : {}),
        };

        this.entries.push(entry);

        if (this.entries.length > MAX_ENTRIES) {
            this.entries.splice(0, this.entries.length - MAX_ENTRIES);
        }
    }

    private now(): number {
        return typeof performance !== 'undefined' && typeof performance.now === 'function'
            ? performance.now()
            : Date.now();
    }

    private readMemory(): DiagnosticMemorySnapshot | undefined {
        if (typeof performance === 'undefined' || !performance.memory) {
            return undefined;
        }

        const memory = performance.memory;
        const snapshot: DiagnosticMemorySnapshot = {
            ...(typeof memory.usedJsHeapSize === 'number' ? { usedJsHeapSize: memory.usedJsHeapSize } : {}),
            ...(typeof memory.totalJsHeapSize === 'number' ? { totalJsHeapSize: memory.totalJsHeapSize } : {}),
            ...(typeof memory.jsHeapSizeLimit === 'number' ? { jsHeapSizeLimit: memory.jsHeapSizeLimit } : {}),
        };

        return Object.keys(snapshot).length > 0 ? snapshot : undefined;
    }

    private stringifyError(error: unknown): string {
        if (error instanceof Error) {
            return error.message;
        }

        return String(error ?? 'Unknown error');
    }

    private round(value: number): number {
        const factor = 10 ** DECIMAL_PLACES;

        return Math.round(value * factor) / factor;
    }
}
